import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Table, Button, Form, Badge, Alert, Spinner, Pagination } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { ordersAPI, reportsAPI, vatAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const Dashboard = () => {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [vatRate, setVatRate] = useState(7);

  // Filters
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Ref to prevent duplicate API calls
  const hasLoadedRef = useRef(false);

  const { user } = useAuth();

  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadData();
      loadDashboardStats();
      loadVatRate();
    }
  }, []);

  const loadData = async (page = currentPage) => {
    try {
      setLoading(true);
      setError('');

      const params = {
        page,
        limit: itemsPerPage,
        lightweight: 'false', // Request full data including delivery and approvedBy
        fulfillmentStatus: 'fulfilled',
        approvalStatus: 'approved'
      };

      const ordersRes = await ordersAPI.getAll(params);

      setOrders(ordersRes.data.data);
      setStats(ordersRes.data.stats);
      setTotalCount(ordersRes.data.totalCount || ordersRes.data.count);
      setTotalPages(ordersRes.data.totalPages || 1);
    } catch (err) {
      setError(err.response?.data?.message || 'เกิดข้อผิดพลาดในการโหลดข้อมูล');
    } finally {
      setLoading(false);
    }
  };

  const loadDashboardStats = async () => {
    try {
      const res = await reportsAPI.getDashboard();
      setDashboardStats(res.data.data);
    } catch (err) {
      console.error('Error loading dashboard stats:', err);
    }
  };

  const loadVatRate = async () => {
    try {
      const res = await vatAPI.get();
      if (res.data.success && res.data.data) {
        setVatRate(parseFloat(res.data.data.rate) || 7);
      }
    } catch (err) {
      console.error('Failed to load VAT rate:', err);
      // Keep default 7% if loading fails
    }
  };

  const calculateItemTotal = (item) => {
    const quantity = item.quantity || 0;
    const unitCost = item.unitCost || item.material?.salePrice || 0;
    const subtotal = quantity * unitCost;

    if (item.hasVat) {
      return subtotal * (1 + vatRate / 100);
    }
    return subtotal;
  };

  const calculateOrderTotal = (order) => {
    if (!order.items || order.items.length === 0) {
      return 0;
    }

    // Calculate subtotals for VAT and non-VAT items
    let vatSubtotal = 0;
    let nonVatSubtotal = 0;

    order.items.forEach(item => {
      const itemTotal = calculateItemTotal(item);
      if (item.hasVat) {
        vatSubtotal += itemTotal;
      } else {
        nonVatSubtotal += itemTotal;
      }
    });

    // Apply discounts (ensure they don't exceed their respective subtotals)
    const discountVat = order.discountVat || 0;
    const discountNonVat = order.discountNonVat || 0;

    const clampedDiscountVat = Math.min(discountVat, vatSubtotal);
    const clampedDiscountNonVat = Math.min(discountNonVat, nonVatSubtotal);

    const vatTotal = Math.max(0, vatSubtotal - clampedDiscountVat);
    const nonVatTotal = Math.max(0, nonVatSubtotal - clampedDiscountNonVat);

    return vatTotal + nonVatTotal;
  };

  const calculateTotalCost = () => {
    return orders.reduce((total, order) => total + calculateOrderTotal(order), 0);
  };

  const handleFilter = async () => {
    try {
      setLoading(true);
      setCurrentPage(1); // Reset to first page when filtering
      const params = {
        page: 1,
        limit: itemsPerPage,
        lightweight: 'false',
        fulfillmentStatus: 'fulfilled',
        approvalStatus: 'approved'
      };

      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;

      const res = await ordersAPI.getAll(params);
      setOrders(res.data.data);
      setStats(res.data.stats);
      setTotalCount(res.data.totalCount || res.data.count);
      setTotalPages(res.data.totalPages || 1);
    } catch (err) {
      setError(err.response?.data?.message || 'เกิดข้อผิดพลาดในการกรองข้อมูล');
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilter = () => {
    setDateFrom('');
    setDateTo('');
    setCurrentPage(1);
    loadData(1);
  };

  const handleQuickFilter = async (type) => {
    const today = new Date();
    let from = '';

    if (type === 'today') {
      from = today.toISOString().split('T')[0];
      setDateFrom(from);
      setDateTo(from);
    } else if (type === 'week') {
      const weekAgo = new Date(today);
      weekAgo.setDate(today.getDate() - 7);
      from = weekAgo.toISOString().split('T')[0];
      setDateFrom(from);
      setDateTo(today.toISOString().split('T')[0]);
    } else if (type === 'month') {
      const monthAgo = new Date(today);
      monthAgo.setMonth(today.getMonth() - 1);
      from = monthAgo.toISOString().split('T')[0];
      setDateFrom(from);
      setDateTo(today.toISOString().split('T')[0]);
    }

    try {
      setLoading(true);
      setCurrentPage(1); // Reset to first page when filtering
      const params = {
        page: 1,
        limit: itemsPerPage,
        lightweight: 'false',
        fulfillmentStatus: 'fulfilled',
        approvalStatus: 'approved'
      };
      if (from) params.dateFrom = from;
      if (type === 'today' || type === 'week' || type === 'month') {
        params.dateTo = today.toISOString().split('T')[0];
      }

      const res = await ordersAPI.getAll(params);
      setOrders(res.data.data);
      setStats(res.data.stats);
      setTotalCount(res.data.totalCount || res.data.count);
      setTotalPages(res.data.totalPages || 1);
    } catch (err) {
      setError(err.response?.data?.message || 'เกิดข้อผิดพลาด');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('คุณแน่ใจหรือไม่ที่จะลบคำสั่งซื้อนี้?')) {
      return;
    }

    try {
      await ordersAPI.delete(id);
      loadData();
      alert('ลบคำสั่งซื้อสำเร็จ');
    } catch (err) {
      alert(err.response?.data?.message || 'เกิดข้อผิดพลาดในการลบคำสั่งซื้อ');
    }
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    loadData(pageNumber);
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: <Badge bg="warning" text="dark">รอดำเนินการ</Badge>,
      approved: <Badge bg="success">อนุมัติแล้ว</Badge>,
      rejected: <Badge bg="danger">ปฏิเสธ</Badge>
    };
    return badges[status] || <Badge bg="secondary">{status}</Badge>;
  };

  const getFulfillmentBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge bg="secondary">รอดำเนินการ</Badge>;
      case 'reserved':
        return <Badge bg="info">จองสต็อกแล้ว</Badge>;
      case 'picking':
        return <Badge bg="primary">กำลังดำเนินการ</Badge>;
      case 'fulfilled':
        return <Badge bg="success">เสร็จสมบูรณ์</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB'
    }).format(amount);
  };

  if (loading && orders.length === 0) {
    return (
      <Container className="main-content text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">กำลังโหลดข้อมูล...</p>
      </Container>
    );
  }

  return (
    <Container fluid className="main-content">
      <Row className="mb-4">
        <Col>
          <h2>
            <i className="bi bi-house-door me-2"></i>
            หน้าแรก
          </h2>
          <p className="text-muted">คำสั่งซื้อที่จัดส่งเสร็จสิ้นแล้ว</p>
        </Col>
        {/* Only branch users can create orders */}
        {!user?.isSuperuser && !user?.isStaff && (
          <Col xs="auto">
            <Link to="/orders/create">
              <Button variant="primary" size="lg">
                <i className="bi bi-plus-circle me-2"></i>
                สร้างคำสั่งซื้อใหม่
              </Button>
            </Link>
          </Col>
        )}
      </Row>

      {/* Dashboard Stats */}
      {dashboardStats && (
        <Row className="mb-4">
          <Col md={3}>
            <Card className="stats-card">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="text-muted mb-1">คำสั่งซื้อทั้งหมด</p>
                    <h3 className="mb-0">{dashboardStats.totalOrders}</h3>
                  </div>
                  <div className="text-primary">
                    <i className="bi bi-clipboard-check" style={{ fontSize: '2rem' }}></i>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="stats-card">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="text-muted mb-1">รอดำเนินการ</p>
                    <h3 className="mb-0 text-warning">{dashboardStats.pendingOrders}</h3>
                  </div>
                  <div className="text-warning">
                    <i className="bi bi-clock-history" style={{ fontSize: '2rem' }}></i>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="stats-card">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="text-muted mb-1">อนุมัติแล้ว</p>
                    <h3 className="mb-0 text-success">{dashboardStats.approvedOrders}</h3>
                  </div>
                  <div className="text-success">
                    <i className="bi bi-check-circle" style={{ fontSize: '2rem' }}></i>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="stats-card">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="text-muted mb-1">วันนี้</p>
                    <h3 className="mb-0">{dashboardStats.todayOrders}</h3>
                  </div>
                  <div className="text-info">
                    <i className="bi bi-calendar-check" style={{ fontSize: '2rem' }}></i>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Filters */}
      <Card className="filter-panel mb-4">
        <Card.Body>
          <h5 className="mb-3">
            <i className="bi bi-funnel me-2"></i>
            กรองข้อมูล
          </h5>

          <Row className="mb-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label>วันที่เริ่มต้น</Form.Label>
                <Form.Control
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>วันที่สิ้นสุด</Form.Label>
                <Form.Control
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col>
              <Button variant="primary" onClick={handleFilter} className="me-2">
                <i className="bi bi-search me-2"></i>
                กรอง
              </Button>
              <Button variant="secondary" onClick={handleClearFilter} className="me-2">
                <i className="bi bi-x-circle me-2"></i>
                ล้างตัวกรอง
              </Button>

              <Button variant="outline-primary" size="sm" onClick={() => handleQuickFilter('today')} className="me-2">
                วันนี้
              </Button>
              <Button variant="outline-primary" size="sm" onClick={() => handleQuickFilter('week')} className="me-2">
                7 วันที่แล้ว
              </Button>
              <Button variant="outline-primary" size="sm" onClick={() => handleQuickFilter('month')}>
                30 วันที่แล้ว
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Order Stats */}
      {stats && (
        <Card className="mb-4">
          <Card.Body>
            <Row className="text-center">
              <Col>
                <h5>คำสั่งซื้อที่จัดส่งแล้ว</h5>
                <h3 className="text-primary">{stats.totalOrders}</h3>
              </Col>
              <Col>
                <h5>มูลค่ารวม</h5>
                <h3 className="text-info">{formatCurrency(calculateTotalCost())}</h3>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* Orders Table */}
      <Card>
        <Card.Header>
          <h5 className="mb-0">
            <i className="bi bi-list-ul me-2"></i>
            รายการคำสั่งซื้อ ({orders.length})
          </h5>
        </Card.Header>
        <Card.Body className="p-0">
          {orders.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-inbox" style={{ fontSize: '3rem', color: '#ccc' }}></i>
              <p className="text-muted mt-3">ไม่พบคำสั่งซื้อ</p>
            </div>
          ) : (
            <Table responsive hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>รหัสคำสั่งซื้อ</th>
                  <th>ผู้สั่ง</th>
                  <th>วันที่สร้าง</th>
                  <th className="text-center">จำนวนรายการ</th>
                  <th className="text-end">มูลค่า</th>
                  <th>สถานะอนุมัติ</th>
                  <th>สถานะการหยิบ</th>
                  <th>วันที่จัดส่ง</th>
                  <th>ผู้อนุมัติ / หมายเหตุ</th>
                  <th className="text-center">การจัดการ</th>
                </tr>
              </thead>
              <tbody>
                {orders.map(order => (
                  <tr key={order.id}>
                    <td>
                      <strong>#{order.id}</strong>
                    </td>
                    <td>
                      <i className="bi bi-person me-1"></i>
                      {order.orderedBy?.fullName || order.orderedBy?.username}
                    </td>
                    <td>
                      <i className="bi bi-calendar me-1"></i>
                      {formatDate(order.createdAt)}
                    </td>
                    <td className="text-center">{order.items?.length || 0} รายการ</td>
                    <td className="text-end"><strong>{formatCurrency(calculateOrderTotal(order))}</strong></td>
                    <td>{getStatusBadge(order.approvalStatus)}</td>
                    <td>{getFulfillmentBadge(order.fulfillmentStatus)}</td>
                    <td>
                      {order.delivery?.deliveryDate ? (
                        <div>
                          <i className="bi bi-truck me-1 text-success"></i>
                          <strong>{formatDate(order.delivery.deliveryDate)}</strong>
                          {order.delivery.note && (
                            <div className="text-muted small mt-1">
                              <i className="bi bi-chat-left-text me-1"></i>
                              {order.delivery.note}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>
                      {order.approvedBy ? (
                        <>
                          <div>
                            <i className="bi bi-person-check me-1"></i>
                            {order.approvedBy.fullName || order.approvedBy.username}
                          </div>
                          <small className="text-muted">
                            <i className="bi bi-clock me-1"></i>
                            {new Date(order.approvedAt).toLocaleString('th-TH')}
                          </small>
                          {order.note && order.approvalStatus === 'rejected' && (
                            <>
                              <hr className="my-1" />
                              <div className="text-danger small">
                                <i className="bi bi-exclamation-circle me-1"></i>
                                <strong>เหตุผล:</strong> {order.note}
                              </div>
                            </>
                          )}
                          {order.note && order.approvalStatus === 'approved' && (
                            <>
                              <hr className="my-1" />
                              <div className="text-muted small">
                                <i className="bi bi-chat-left-text me-1"></i>
                                {order.note}
                              </div>
                            </>
                          )}
                        </>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td className="text-center">
                      <Link to={`/orders/${order.id}`}>
                        <Button variant="info" size="sm" className="me-1" title="ดูรายละเอียด">
                          <i className="bi bi-eye"></i>
                        </Button>
                      </Link>

                      {/* Duplicate button - For branch users on approved orders */}
                      {!user?.isSuperuser && !user?.isStaff && order.approvalStatus === 'approved' && (
                        <Link
                          to="/orders/create"
                          state={{ duplicateFrom: order }}
                        >
                          <Button variant="success" size="sm" className="me-1" title="คัดลอกคำสั่งซื้อ">
                            <i className="bi bi-copy"></i>
                          </Button>
                        </Link>
                      )}

                      {/* Edit/Delete buttons - Only for non-approved orders and branch users */}
                      {!user?.isSuperuser && order.approvalStatus !== 'approved' && (
                        <>
                          <Link to={`/orders/${order.id}/edit`}>
                            <Button variant="outline-primary" size="sm" className="me-1" title="แก้ไข">
                              <i className="bi bi-pencil"></i>
                            </Button>
                          </Link>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDelete(order.id)}
                            title="ลบ"
                          >
                            <i className="bi bi-trash"></i>
                          </Button>
                        </>
                      )}

                      {/* For headquarters - show edit/delete for non-approved orders */}
                      {user?.isSuperuser && order.approvalStatus !== 'approved' && (
                        <>
                          <Link to={`/orders/${order.id}/edit`}>
                            <Button variant="outline-primary" size="sm" className="me-1" title="แก้ไข">
                              <i className="bi bi-pencil"></i>
                            </Button>
                          </Link>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDelete(order.id)}
                            title="ลบ"
                          >
                            <i className="bi bi-trash"></i>
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}

          {/* Pagination */}
          {!loading && orders.length > 0 && totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center p-3 border-top">
              <div className="text-muted">
                แสดง {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, totalCount)} จาก {totalCount} รายการ
              </div>
              <Pagination className="mb-0">
                <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
                <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />

                {[...Array(totalPages)].map((_, index) => {
                  const pageNumber = index + 1;
                  // Show first page, last page, current page, and pages around current
                  if (
                    pageNumber === 1 ||
                    pageNumber === totalPages ||
                    (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                  ) {
                    return (
                      <Pagination.Item
                        key={pageNumber}
                        active={pageNumber === currentPage}
                        onClick={() => handlePageChange(pageNumber)}
                      >
                        {pageNumber}
                      </Pagination.Item>
                    );
                  } else if (pageNumber === currentPage - 2 || pageNumber === currentPage + 2) {
                    return <Pagination.Ellipsis key={pageNumber} disabled />;
                  }
                  return null;
                })}

                <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
              </Pagination>
            </div>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default Dashboard;
